// Subscription contract related helpers
import { RRule, datetime } from "rrule";

import { BillingFrequency } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/contracts_pb";
import { SubscriptionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

export const contractTermsInMonths = [1, 12, 24, 36];

export const mapBillingFrequencyToMonths = (
  billingFrequency: BillingFrequency | undefined
) => {
  switch (billingFrequency) {
    case BillingFrequency.BILLING_FREQUENCY_MONTHLY:
      return 1;
    case BillingFrequency.BILLING_FREQUENCY_QUARTERLY:
      return 3;
    case BillingFrequency.BILLING_FREQUENCY_SEMI_ANNUAL:
      return 6;
    case BillingFrequency.BILLING_FREQUENCY_ANNUAL:
      return 12;
    case BillingFrequency.BILLING_FREQUENCY_24_MONTHS:
      return 24;
    case BillingFrequency.BILLING_FREQUENCY_36_MONTHS:
      return 36;
    default:
      return 12;
  }
};

export const mapSubPlanToContractTerm = (
  subType: SubscriptionType | undefined
) => {
  switch (subType) {
    case SubscriptionType.PREMIUM_MONTHLY:
      return 12;
    case SubscriptionType.PREMIUM_QUARTERLY:
      return 12;
    case SubscriptionType.PREMIUM_BIANNUAL:
      return 12;
    case SubscriptionType.PREMIUM_ANNUAL:
      return 12;
    case SubscriptionType.PREMIUM_TWO_YEAR:
      return 24;
    case SubscriptionType.PREMIUM_THREE_YEAR:
      return 36;
    default:
      return 0;
  }
};

export const getMaxContractEndDate = (
  contractTermInMonths: number | undefined
) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  // Default to annual if undefined
  let maxMonth = month + 2 * (contractTermInMonths ?? 12);
  let maxYear = year;

  // If max month is greater than 12, add the difference to the year
  if (maxMonth >= 12) {
    maxYear += Math.floor(maxMonth / 12);
    // Month needs to be between 0 and 11 for JS Date
    maxMonth %= 12;
  }

  const maxDate = new Date(maxYear, maxMonth, now.getDate());

  return maxDate;
};

// Min contract end date is 2 days from now because you true up is processed 24 hours before renewal
// In other words, it is ok to have a contract start date in the past.
export const getMinContractEndDate = () => {
  const now = new Date();
  const minDate = new Date(now.setDate(now.getDate() + 2));

  return minDate;
};

// rrule skips months when a month does not have the day required
// if a schedule is every 31st of the month, then months with 28-30 days will be skipped
// https://stackoverflow.com/questions/35757778/rrule-for-repeating-monthly-on-the-31st-or-closest-day/35765662#35765662
const setByMonthDate = (date: Date) => {
  const day = date.getDate();
  let byMonthDay: number[] = [day];
  switch (day) {
    case 29:
      return (byMonthDay = [28, 29]);
    case 30:
      return (byMonthDay = [28, 29, 30]);
    case 31:
      return (byMonthDay = [28, 29, 30, 31]);
  }

  return byMonthDay;
};

export const retrieveBillingDateOccurrences = ({
  now,
  startDate,
  endDate,
  interval,
}: {
  now: Date;
  startDate: Date;
  endDate: Date;
  interval: number;
}) => {
  const utcStart = Date.UTC(
    startDate.getUTCFullYear(),
    startDate.getUTCMonth(),
    startDate.getUTCDay()
  );
  const newUTCDate = new Date(utcStart);
  const rule = new RRule({
    freq: RRule.MONTHLY,
    interval: interval,
    dtstart: newUTCDate,
    bymonthday: setByMonthDate(startDate),
    bysetpos: -1,
  });
  const dates = rule.between(now, endDate, false);
  return rule && endDate ? dates : [];
};

export const retrieveNextBillingDate = ({
  date,
  interval,
}: {
  date: Date;
  interval: number;
}) => {
  // date counts month from 0, while date time counts from 1. Hence, we need to add 1 to the month
  const newDate = datetime(
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate()
  );
  const rule = new RRule({
    freq: RRule.MONTHLY,
    interval: interval,
    dtstart: newDate,
    bymonthday: setByMonthDate(date),
    bysetpos: -1,
  });

  if (interval === 1) {
    // this is a special case for monthly billing, the flag to exclude dt in rule.after is not actually skipping the date passed
    // which results in the same date being returned, so we need to add  a few days to the date to get the next month occurrence
    const futureDay = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate() + 3
    );
    return rule.after(futureDay, false);
  }
  return rule && newDate ? rule.after(newDate, false) : new Date();
};
